import {
  CreateBillPaymentDocumentRequest,
  InstructionSubmitRequest,
  useUtxBillPaymentCreateBillPaymentDocument,
  useUtxBillPaymentGetEmailInstruction,
  useUtxInstructionApiPostInstructionQuoteRequest,
  useUtxInstructionApiPostInstructionSubmit,
  useUtxInstructionApiSearch,
  useUtxUsersUploadDocument,
  UtxBillPaymentCreateBillPaymentDocumentMutationError,
  UtxBillPaymentCreateBillPaymentDocumentMutationResult,
  UtxInstructionApiPostInstructionQuoteRequestMutationError,
  UtxInstructionApiPostInstructionQuoteRequestMutationResult,
  UtxInstructionApiPostInstructionSubmitMutationError,
  UtxInstructionApiPostInstructionSubmitMutationResult,
  UtxUsersUploadDocumentBody,
  UtxUsersUploadDocumentMutationError,
  UtxUsersUploadDocumentMutationResult
} from '@utx/utx-client';
import Constants from 'expo-constants';
import * as DocumentPicker from 'expo-document-picker';
import _ from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import { BillPaymentProp } from '../payment.navigator';
import { delay } from '../utils/timing';
import { isAmountPositive } from '../utils/utils';
import { UseBillPaymentFormProp } from './useBillPaymentForm.hook';

export interface Verification {
  code: string;
}

export interface QuoteResponse {
  id?: string;
  intent?: string;
  idempotencyKey?: string;
  purpose?: string;
  remarks?: string;
  feeAmount?: number;
  feeSnapshotId?: string;
  classified?: boolean;
  attributes?: {
    amount?: number;
    asset?: string;
    paymentType?: string;
    attachments?: Attachment[];
    source?: QuoteDataSource;
    destination?: QuoteDestination;
    schedule?: {
      type?: string;
      startDate?: string;
      endDate?: string;
      interval?: string;
    };
  };
}

export interface SearchQuoteResponse {
  current: number;
  data: QuoteResponse[];
  perPage: number;
  total: number;
  totalPages: number;
}

export interface Attachment {
  url: string;
  fileName: string;
  isDeleted: boolean;
  thumbnailUrl?: string;
  clientDocumentId: string;
}

interface QuoteDataSource {
  type: string;
  attributes?: {
    walletId?: string;
  };
}

interface QuoteDestination {
  type: string;
  attributes?: {
    saveTransferPeer?: boolean;
    label?: string;
    transferPeerId?: string;
    recipient?: string;
    accountNumber?: string;
    paymentType?: string;
    paymentInfo?: string;
  };
}
export interface QuoteDataRequest {
  id?: string;
  intent: string;
  idempotencyKey: string;
  purpose?: string;
  remarks?: string;
  attributes: {
    amount?: string;
    asset?: string;
    attachments: Attachment[];
    source?: QuoteDataSource;
    destination?: QuoteDestination;
    schedule?: {
      type?: string;
      startDate?: string;
      endDate?: string;
      interval?: string;
    };
  };
}

export enum VERIFICATION_CODE {
  PAYMENT_NOT_CLASSIFIED = 'PAYMENT_NOT_CLASSIFIED',
  INSUFFICIENT_BALANCE = 'INSUFFICIENT_BALANCE',
  WALLET_NOT_FOUND = 'WALLET_NOT_FOUND'
}

export interface UseCreateBillPaymentProp {
  general: {
    alertMessage: string;
    paymentType: PAYMENT_TYPE | undefined;
    setAlertMessage: (input: string) => void;
    getThumbnailUrLFromResponse: (response: QuoteResponse) => string;
    setPaymentType: (input: PAYMENT_TYPE) => void;
    mapSearchWithResponse: (response: QuoteResponse, search: QuoteResponse) => QuoteResponse;
  };
  instruction: {
    urlDocument: string;
    documentName: string;
    statusButtonQuote: boolean;
    documentSelected: DocumentPicker.DocumentResult | undefined;
    emailInstructionData: string;
    setUrlDocument: (input: string) => void;
    setDocumentName: (input: string) => void;
    setDocumentId: (input: string) => void;
    setStatusButtonQuote: (input: boolean) => void;
    setLoopQuote: (input: boolean) => void;
    setNextParamProp: (input: BillPaymentProp | undefined) => void;
    setDocumentSelected: (input: DocumentPicker.DocumentResult | undefined) => void;
    postUploadDocument: (
      request: UtxUsersUploadDocumentBody,
      onSuccess: (data: UtxUsersUploadDocumentMutationResult) => void,
      onError: (error: UtxUsersUploadDocumentMutationError) => void
    ) => void;
    runPostQuote: () => Promise<void>;
    postQuote: (
      request: QuoteDataRequest,
      onSuccess: (data: UtxInstructionApiPostInstructionQuoteRequestMutationResult) => void,
      onError: (error: UtxInstructionApiPostInstructionQuoteRequestMutationError) => void
    ) => void;
    postCreateBillPayment: (
      request: CreateBillPaymentDocumentRequest,
      onSuccess: (data: UtxBillPaymentCreateBillPaymentDocumentMutationResult) => void,
      onError: (error: UtxBillPaymentCreateBillPaymentDocumentMutationError) => void
    ) => void;
    mapQuoteDataRequest: (request: QuoteDataRequest, response: QuoteResponse) => QuoteDataRequest;
    resetQuote: () => void;
    fillFormValuesToQuotedRequest: (bpForm: UseBillPaymentFormProp, quoteRequest: QuoteDataRequest) => QuoteDataRequest;
    isLoadingUpload: boolean;
    isSuccessUpload: boolean;
    isLoadingQuote: boolean;
    resetChooseNewDocument: () => void;
  };
  postCreateBillPayment: {
    mapInstructionSubmitRequest: (request: QuoteDataRequest, response: QuoteResponse, otpCode: string | null) => InstructionSubmitRequest;
    postSubmit: (
      request: InstructionSubmitRequest,
      onSuccess: (data: UtxInstructionApiPostInstructionSubmitMutationResult) => void,
      onError: (error: UtxInstructionApiPostInstructionSubmitMutationError) => void
    ) => void;
    nextParamProp: BillPaymentProp | undefined;
    isLoadingSubmit: boolean;
  };
}

export enum PAYMENT_TYPE {
  GLOBAL = 'GLOBAL',
  LOCAL = 'LOCAL'
}

export const useCreateBillPayment = (): UseCreateBillPaymentProp => {
  const { t } = useTranslation('global-bill-payment');
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [paymentType, setPaymentType] = useState<PAYMENT_TYPE>();
  const [paramProp, setParamProp] = useState<BillPaymentProp>();
  const [loadingQuote, setLoadingQuote] = useState<boolean>(false);
  const [statusButtonQuote, setStatusButtonQuote] = useState<boolean>(false);
  const documentName = useRef('');
  const documentId = useRef('');
  const instructionId = useRef('');
  const idempotencyKey = useRef<string>();
  const hookRetry = useRef<number>(Constants.manifest?.extra?.billPayment?.retry);
  const searchQuoteDataResponse = useRef<SearchQuoteResponse>();
  const quoteRequest = useRef<QuoteDataRequest>();
  const setDocumentName = (input: string) => {
    documentName.current = input;
  };
  const setDocumentId = (input: string) => {
    documentId.current = input;
  };
  const urlDocument = useRef('');
  const setUrlDocument = (input: string) => {
    hookRetry.current = Constants.manifest?.extra?.billPayment?.retry;
    urlDocument.current = input;
  };
  const documentSelected = useRef<DocumentPicker.DocumentResult>();
  const setDocumentSelected = (input: DocumentPicker.DocumentResult | undefined) => {
    documentSelected.current = input;
  };
  const continueLoop = useRef(true);
  const setLoopQuote = (isLoop: boolean) => {
    continueLoop.current = isLoop;
  };

  const getQuoteImageRequest = (): QuoteDataRequest => {
    if (!idempotencyKey.current) {
      throw new Error('idempotencyKey NULL');
    }
    return {
      intent: 'PAYMENT',
      idempotencyKey: idempotencyKey.current,
      remarks: '',
      purpose: '',
      attributes: {
        attachments: [{ url: urlDocument.current, isDeleted: false, fileName: documentName.current, clientDocumentId: documentId.current }]
      }
    };
  };

  const quoteQuery = useUtxInstructionApiPostInstructionQuoteRequest();

  const postQuote = (
    request: QuoteDataRequest,
    onSuccess: (data: UtxInstructionApiPostInstructionQuoteRequestMutationResult) => void,
    onError: (error: UtxInstructionApiPostInstructionQuoteRequestMutationError) => void
  ) => {
    quoteQuery.mutate(
      {
        data: request
      },
      {
        onSuccess,
        onError
      }
    );
  };

  const uploadDocumentQuery = useUtxUsersUploadDocument();
  const postUploadDocument = (
    request: UtxUsersUploadDocumentBody,
    onSuccess: (data: UtxUsersUploadDocumentMutationResult) => void,
    onError: (error: UtxUsersUploadDocumentMutationError) => void
  ) => {
    uploadDocumentQuery.mutate(
      {
        data: request
      },
      {
        onSuccess,
        onError
      }
    );
  };

  const submitQuery = useUtxInstructionApiPostInstructionSubmit();
  const postSubmit = (
    request: InstructionSubmitRequest,
    onSuccess: (data: UtxInstructionApiPostInstructionSubmitMutationResult) => void,
    onError: (error: UtxInstructionApiPostInstructionSubmitMutationError) => void
  ) => {
    submitQuery.mutate(
      {
        data: request
      },
      {
        onSuccess,
        onError
      }
    );
  };

  const searchQuery = useUtxInstructionApiSearch(
    {
      pageSize: 20,
      current: 1,
      id: instructionId.current,
      intent: 'PAYMENT'
    },
    {
      query: {
        enabled: false,
        onSuccess: (data) => {
          searchQuoteDataResponse.current = data.data!;
          loopSearchQuote(hookRetry.current);
        },
        onError: (error) => {
          console.log('Error:', error);
          setLoadingQuote(false);
          const errorMessage =
            error.response?.data && _.isString(error.response?.data?.message) ? error.response?.data?.message : error.message;
          setAlertMessage(errorMessage);
          return;
        }
      }
    }
  );

  const createBillPaymentQuery = useUtxBillPaymentCreateBillPaymentDocument();
  const postCreateBillPayment = (
    request: CreateBillPaymentDocumentRequest,
    onSuccess: (data: UtxBillPaymentCreateBillPaymentDocumentMutationResult) => void,
    onError: (error: UtxBillPaymentCreateBillPaymentDocumentMutationError) => void
  ) => {
    createBillPaymentQuery.mutate(
      {
        data: request
      },
      {
        onSuccess,
        onError
      }
    );
  };

  const emailInstructionQuery = useUtxBillPaymentGetEmailInstruction({
    query: {
      enabled: false
    }
  });

  useEffect(() => {
    emailInstructionQuery.refetch();
  }, []);

  const emailInstructionData = useMemo(() => {
    if (!!emailInstructionQuery?.data?.data) return emailInstructionQuery?.data?.data;
    return '';
  }, [emailInstructionQuery?.data?.data]);

  const mapInstructionSubmitRequest = (
    request: QuoteDataRequest,
    response: QuoteResponse,
    otpCode: string | null
  ): InstructionSubmitRequest => {
    return {
      intent: request.intent,
      instructionId: response.id ?? '',
      amount: response?.attributes?.amount?.toString() ?? '0',
      asset: response?.attributes?.asset ?? 'HKD',
      purpose: '',
      remarks: response.remarks ?? '',
      feeSnapshotId: '',
      note: '',
      token: otpCode
    };
  };

  const mapQuoteDataRequest = (request: QuoteDataRequest, response: QuoteResponse): QuoteDataRequest => {
    const attachments: Attachment[] = [
      {
        url: !!response?.attributes?.attachments?.at(0)?.url
          ? response?.attributes?.attachments?.at(0)?.url!
          : request.attributes.attachments[0].url,
        fileName: !!response?.attributes?.attachments?.at(0)?.fileName
          ? response?.attributes?.attachments?.at(0)?.fileName!
          : request.attributes.attachments[0].fileName,
        isDeleted: false,
        clientDocumentId: !!response?.attributes?.attachments?.at(0)?.clientDocumentId
          ? response?.attributes?.attachments?.at(0)?.clientDocumentId!
          : documentId.current
      }
    ];

    const destination: QuoteDestination = {
      type: response?.attributes?.destination?.type ?? 'PAYEE',
      attributes: {
        saveTransferPeer: response?.attributes?.destination?.attributes?.saveTransferPeer,
        label: response?.attributes?.destination?.attributes?.label,
        transferPeerId: response?.attributes?.destination?.attributes?.transferPeerId,
        recipient: response?.attributes?.destination?.attributes?.recipient,
        accountNumber: response?.attributes?.destination?.attributes?.accountNumber,
        paymentType: paymentType,
        paymentInfo: response?.attributes?.destination?.attributes?.paymentInfo
      }
    };

    const source: QuoteDataSource = {
      type: response?.attributes?.source?.type ?? 'ACCOUNT',
      attributes: response?.attributes?.source?.attributes
    };

    return {
      intent: request.intent,
      idempotencyKey: request.idempotencyKey,
      purpose: request.purpose,
      remarks: request.remarks,
      id: response.id,
      attributes: {
        attachments: attachments,
        asset: response?.attributes?.asset,
        amount: isAmountPositive(response?.attributes?.amount) ? response?.attributes?.amount?.toString() : undefined,
        source: source,
        destination: destination
      }
    };
  };

  const mapSearchWithResponse = (response: QuoteResponse, search: QuoteResponse): QuoteResponse => {
    let destination: QuoteDestination | undefined;
    if (!!response?.attributes?.destination?.type) {
      destination = {
        type: response?.attributes?.destination?.type,
        attributes: {
          saveTransferPeer: response?.attributes?.destination?.attributes?.saveTransferPeer,
          label: response?.attributes?.destination?.attributes?.label,
          transferPeerId: response?.attributes?.destination?.attributes?.transferPeerId,
          recipient: response?.attributes?.destination?.attributes?.recipient,
          accountNumber: response?.attributes?.destination?.attributes?.accountNumber,
          paymentType: paymentType,
          paymentInfo: response?.attributes?.destination?.attributes?.paymentInfo
        }
      };
    } else destination = response?.attributes?.destination;
    return {
      ...response,
      attributes: {
        amount: response.attributes?.amount,
        asset: response.attributes?.asset,
        paymentType: response.attributes?.paymentType,
        attachments: search.attributes?.attachments,
        source: response.attributes?.source,
        destination: destination,
        schedule: response.attributes?.schedule
      }
    };
  };

  const validateStopQuote = (response: QuoteResponse): boolean => {
    const validateThumbnail =
      documentSelected.current?.mimeType == 'image/png' ||
      documentSelected.current?.mimeType == 'image/jpeg' ||
      !!response?.attributes?.attachments?.at(0)?.thumbnailUrl;
    const validateAmount = isAmountPositive(response?.attributes?.amount);
    const validateRecipient = !!response?.attributes?.destination?.attributes?.recipient;
    const validateAccountNumber = !!response?.attributes?.destination?.attributes?.accountNumber;
    const validateTransferPeerId = !!response?.attributes?.destination?.attributes?.transferPeerId;

    if (validateThumbnail && (validateAmount || validateRecipient || validateAccountNumber)) {
      return true;
    }

    if (validateThumbnail && validateTransferPeerId) {
      return true;
    }

    return false;
  };

  const runPostQuote = async () => {
    setParamProp(undefined);
    instructionId.current = '';
    if (!paymentType) {
      console.error('INVALID PAYMENT_TYPE');
      return;
    }
    setLoadingQuote(true);
    const rq: QuoteDataRequest = getQuoteImageRequest();
    quoteRequest.current = rq;
    await postQuote(
      rq,
      async (data) => {
        const response: QuoteResponse = data.data;
        if (!!response) {
          if (!!response && response.classified) {
            setParamProp({
              request: mapQuoteDataRequest(rq, response),
              response,
              documentSelected: documentSelected.current,
              paymentType: paymentType!
            });
            setLoadingQuote(false);
            return;
          } else {
            if (!response.id) {
              console.log('QUOTE RESPONSE ID NULL OR EMPTY');
              setLoadingQuote(false);
              return;
            }
            instructionId.current = response.id;
            if (validateStopQuote(response)) {
              setParamProp({
                request: mapQuoteDataRequest(rq, response),
                response,
                documentSelected: documentSelected.current,
                paymentType: paymentType!
              });
              setLoadingQuote(false);
              return;
            }
            await delay(Constants.manifest?.extra?.billPayment?.delay < 3000 ? 3000 : Constants.manifest?.extra?.billPayment?.delay);
            setLoopQuote(true);
            if (!!instructionId.current && response.idempotencyKey == idempotencyKey.current) searchQuery.refetch();
          }
        }
      },
      (error) => {
        console.log('Error:', error);
        setLoadingQuote(false);
        const errorMessage =
          error.response?.data && _.isString(error.response?.data?.message) ? error.response?.data?.message : error.message;
        setAlertMessage(errorMessage);
        return;
      }
    );
  };

  const loopSearchQuote = async (retry: number) => {
    setParamProp(undefined);
    if (!continueLoop.current || !instructionId.current) return;
    if (!searchQuoteDataResponse.current) {
      if (retry > 1 && continueLoop.current) {
        hookRetry.current = hookRetry.current - 1;
        searchQuery.refetch();
        return;
      } else {
        console.log('INVALID LOOP QUOTE');
        setLoadingQuote(false);
        return;
      }
    }

    if (retry === 1 || validateStopQuote(searchQuoteDataResponse.current.data[0])) {
      if (!searchQuoteDataResponse.current.data[0]) {
        setAlertMessage(t('global-bill-payment::glossary.data-not-received-error'));
        setLoadingQuote(false);
        setStatusButtonQuote(false);
        return;
      }
      setParamProp({
        request: mapQuoteDataRequest(quoteRequest.current!, searchQuoteDataResponse.current.data[0]),
        response: searchQuoteDataResponse.current.data[0],
        documentSelected: documentSelected.current,
        paymentType: paymentType!
      });
      setLoadingQuote(false);
      setStatusButtonQuote(false);
      return;
    }

    if (retry > 1 && continueLoop.current) {
      await delay(Constants.manifest?.extra?.billPayment?.delay);
      if (!!searchQuoteDataResponse.current.data.at(0)?.idempotencyKey) {
        if (searchQuoteDataResponse.current.data.at(0)?.idempotencyKey != idempotencyKey.current) return;
      }
      if (continueLoop.current) {
        hookRetry.current = hookRetry.current - 1;
        searchQuery.refetch();
        return;
      }
      return;
    } else {
      setParamProp({
        request: mapQuoteDataRequest(quoteRequest.current!, searchQuoteDataResponse.current.data[0]),
        response: searchQuoteDataResponse.current.data[0],
        documentSelected: documentSelected.current,
        paymentType: paymentType!
      });
      setLoadingQuote(false);
      return;
    }
  };

  const resetQuote = () => {
    if (!loadingQuote && !statusButtonQuote && hookRetry.current <= 1) {
      if (!!paramProp?.response) {
        setStatusButtonQuote(true);
      } else {
        hookRetry.current = Constants.manifest?.extra?.billPayment?.retry;
        setLoadingQuote(true);
        setStatusButtonQuote(true);
        setAlertMessage('');
        searchQuery.refetch();
      }
    } else {
      setStatusButtonQuote(true);
    }
  };

  const getThumbnailUrLFromResponse = (response: QuoteResponse): string => {
    if (!!response?.attributes?.attachments?.at(0)?.thumbnailUrl) {
      return response?.attributes?.attachments?.at(0)?.thumbnailUrl!;
    }
    return '';
  };

  const resetChooseNewDocument = () => {
    idempotencyKey.current = v4();
    setAlertMessage('');
    setStatusButtonQuote(false);
    setLoopQuote(false);
    setParamProp(undefined);
  };

  const fillFormValuesToQuotedRequest = (bpForm: UseBillPaymentFormProp, quoteRequest: QuoteDataRequest) => {
    const form = bpForm.detailForm;
    if (!quoteRequest.attributes || !quoteRequest.attributes.destination || !quoteRequest.attributes.destination.attributes)
      return quoteRequest;
    if (!!form.amount) quoteRequest.attributes.amount = form.amount;
    if (!!form.bank) quoteRequest.attributes.destination.attributes.recipient = form.bank;
    if (!!form.bankAccountNumber) quoteRequest.attributes.destination.attributes.accountNumber = form.bankAccountNumber;
    if (!!form.additionalDetail) quoteRequest.attributes.destination.attributes.paymentInfo = form.additionalDetail;
    return quoteRequest;
  };

  return {
    general: {
      alertMessage,
      paymentType,
      setAlertMessage,
      getThumbnailUrLFromResponse,
      setPaymentType,
      mapSearchWithResponse
    },
    instruction: {
      urlDocument: urlDocument.current,
      documentName: documentName.current,
      statusButtonQuote,
      documentSelected: documentSelected.current,
      emailInstructionData,
      setUrlDocument,
      setDocumentName,
      setDocumentId,
      setStatusButtonQuote,
      setDocumentSelected,
      postUploadDocument,
      setNextParamProp: setParamProp,
      postQuote,
      runPostQuote,
      setLoopQuote,
      postCreateBillPayment,
      mapQuoteDataRequest,
      resetQuote,
      resetChooseNewDocument,
      fillFormValuesToQuotedRequest,
      isLoadingUpload: uploadDocumentQuery.isLoading,
      isSuccessUpload: uploadDocumentQuery.isSuccess,
      isLoadingQuote: quoteQuery.isLoading || loadingQuote
    },
    postCreateBillPayment: {
      mapInstructionSubmitRequest,
      postSubmit,
      nextParamProp: paramProp,
      isLoadingSubmit: submitQuery.isLoading
    }
  };
};
